import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Search } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import type { AdminFeedbackListItem } from "@shared-core/data/api-types";
import { paths } from "@shared-core/routing/routes";
import { Input } from "../app/components/primitives/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { ListPagination } from "../app/components/patterns/ListPagination";

const PAGE_SIZE = 25;

export function SettingsFeedbackPage() {
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const searchQuery = searchParams.get("q") ?? "";

  const [items, setItems] = useState<AdminFeedbackListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState(searchQuery);

  const debouncedSearch = useDebouncedValue(search, 300);

  function setPage(p: number) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(p));
      return next;
    });
  }

  // Reset page when search changes
  useEffect(() => {
    if (debouncedSearch !== searchQuery) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch) {
          next.set("q", debouncedSearch);
        } else {
          next.delete("q");
        }
        next.set("page", "1");
        return next;
      });
    }
  }, [debouncedSearch, searchQuery, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    adapter
      .listAdminFeedback({
        page,
        pageSize: PAGE_SIZE,
        sort: "-created_at",
        q: debouncedSearch || undefined,
      })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
        setTotalPages(res.total_pages);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, page, debouncedSearch]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Feedback</h2>
          <p className="text-sm text-muted-foreground">
            {total} item{total !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input
          placeholder="Search feedback..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Date</TableHead>
              <TableHead className="w-[120px]">User</TableHead>
              <TableHead>Summary</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="text-center text-muted-foreground py-8"
                >
                  {debouncedSearch
                    ? "No feedback matching your search."
                    : "No feedback submitted yet."}
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow
                  key={item.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() =>
                    navigate(
                      paths.settingsFeedbackDetail({ feedbackId: item.id }),
                    )
                  }
                >
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatDateOrDash(item.created_at)}
                  </TableCell>
                  <TableCell className="text-sm font-medium">
                    {item.user_email ?? item.user_id}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">
                        {item.summary_headline ?? "Feedback"}
                      </span>
                      {item.mode === "brain_sucker" && (
                        <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                          Brain Sucker
                        </span>
                      )}
                    </div>
                    {item.summary_preview && (
                      <div className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                        {item.summary_preview}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <ListPagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
